import type { ThemeConfig } from 'antd';
import { Azure, BarahColors, Gold, LightTheme, Radius, Semantic, Shadow, Typography } from '@barahpay/shared';

/**
 * The Barah brand expressed as an Ant Design theme.
 *
 * Ant Design derives most of its palette from a handful of seed tokens, so setting those is what makes the
 * whole portal — buttons, tags, tables, date pickers, notifications — read as Barah rather than as default
 * antd blue. Component-level overrides below are limited to the places where the brand asks for something
 * antd would not derive: the pill navigation, the navy active state, and the card radius.
 */
export const barahTheme: ThemeConfig = {
  token: {
    // Gold is the brand accent, so it is the primary seed. Navy carries text and dark surfaces.
    colorPrimary: Gold[500],
    colorInfo: Azure[500],
    colorSuccess: Semantic.success,
    colorWarning: Semantic.warning,
    colorError: Semantic.error,

    colorTextBase: LightTheme.textPrimary,
    colorBgBase: LightTheme.surface,
    colorBgLayout: LightTheme.background,
    colorBorder: LightTheme.border,
    colorBorderSecondary: LightTheme.borderLight,

    fontFamily: Typography.fontFamily,
    fontSize: Typography.fontSizeBase,

    // The brand's base radius is 8px, with 16px on cards and buttons.
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    controlHeight: 36,
    wireframe: false,

    boxShadow: Shadow.sm,
    boxShadowSecondary: Shadow.md,
  },

  components: {
    Layout: {
      bodyBg: LightTheme.background,
      headerBg: LightTheme.surface,
      headerHeight: 64,
      siderBg: 'transparent',
      footerBg: 'transparent',
    },

    /*
     * The Meezan "soft floating card" navigation: every item is a full pill, and the active leaf is a navy
     * pill with white text rather than antd's tinted-blue row. Gold is the accent everywhere else, so navy
     * here keeps the selected item from competing with the primary buttons on the page.
     */
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemBorderRadius: Number.parseFloat(Radius.full),
      itemHeight: 40,
      itemMarginBlock: 4,
      itemMarginInline: 8,
      itemPaddingInline: 14,

      itemColor: LightTheme.textSecondary,
      itemHoverBg: LightTheme.backgroundSecondary,
      itemHoverColor: LightTheme.textPrimary,

      itemSelectedBg: BarahColors.navy,
      itemSelectedColor: '#FFFFFF',

      groupTitleColor: LightTheme.textMuted,
      groupTitleFontSize: 11,

      iconSize: 17,
      collapsedIconSize: 18,
    },

    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
      headerBg: 'transparent',
      boxShadowTertiary: Shadow.sm,
    },

    Button: {
      // 16px, per the brand button spec.
      borderRadius: 12,
      borderRadiusLG: 14,
      borderRadiusSM: 10,
      fontWeight: 500,
      primaryShadow: Shadow.gold,
      // Gold is light, so navy text reads better on it than white.
      primaryColor: BarahColors.navy,
    },

    Input: {
      borderRadius: 10,
      paddingBlock: 7,
      activeShadow: `0 0 0 3px rgba(212, 168, 67, 0.15)`,
    },

    Select: { borderRadius: 10 },
    DatePicker: { borderRadius: 10, activeShadow: `0 0 0 3px rgba(212, 168, 67, 0.15)` },

    Table: {
      headerBg: LightTheme.backgroundSecondary,
      headerColor: LightTheme.textSecondary,
      headerSplitColor: 'transparent',
      rowHoverBg: Gold[50],
      borderColor: LightTheme.borderLight,
      cellPaddingBlockSM: 10,
    },

    Tag: { borderRadiusSM: Number.parseFloat(Radius.full) * 1, defaultBg: LightTheme.backgroundSecondary },

    Tabs: { itemSelectedColor: BarahColors.navy, inkBarColor: Gold[500], titleFontSize: 14 },

    Statistic: { titleFontSize: 12, contentFontSize: 22 },

    Drawer: { paddingLG: 24 },
    Modal: { borderRadiusLG: 16 },
    Badge: { textFontSize: 11 },
    Segmented: { itemSelectedBg: BarahColors.navy, itemSelectedColor: '#FFFFFF', borderRadius: 8 },
  },
};
